import { useState, useRef, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";
import { statusBadge } from "@/lib/data";

interface InlineStatusBadgeProps {
  status: string;
  options: string[];
  onChange: (newStatus: string) => Promise<void> | void;
  disabled?: boolean;
}

export default function InlineStatusBadge({ status, options, onChange, disabled }: InlineStatusBadgeProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleChange = async (newStatus: string) => {
    if (newStatus === status) { setOpen(false); return; }
    setLoading(true);
    setOpen(false);
    try { await onChange(newStatus); } finally { setLoading(false); }
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled || loading}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium capitalize transition-all ${statusBadge(status)} ${
          disabled ? "cursor-default" : "cursor-pointer hover:opacity-80 hover:shadow-sm"
        } ${loading ? "opacity-60" : ""}`}
      >
        {loading ? (
          <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          status.replace(/_/g, " ")
        )}
        {!disabled && !loading && <ChevronDown className="w-2.5 h-2.5 opacity-60" />}
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-stone-200 rounded-xl shadow-xl overflow-hidden min-w-[140px]">
          {options.map(opt => (
            <button
              key={opt}
              onClick={() => handleChange(opt)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs text-left hover:bg-stone-50 transition-colors"
            >
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium capitalize ${statusBadge(opt)}`}>
                {opt.replace(/_/g, " ")}
              </span>
              {opt === status && <Check className="w-3 h-3 text-amber-600 flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
